import React, { useState } from "react";
import { Link } from "react-router-dom";
import { adminApi, formatApiErrorDetail } from "../lib/api";
import { NaaviLogo, ShapeSquare, ShapeCircle } from "../components/brand/Shapes";
import { ShieldCheck, ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [ttl, setTtl] = useState(30);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await adminApi.post("/admin/forgot-password", { email });
      setTtl(data.ttl_minutes || 30);
      setSent(true);
      toast.success("If an account exists, a reset link is on its way.");
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0] relative overflow-hidden flex items-center justify-center px-5 py-10" data-testid="admin-forgot-password-page">
      <ShapeSquare size={220} className="absolute -top-16 -left-16 opacity-90 rotate-12" color="#648DE5" />
      <ShapeCircle size={140} className="absolute bottom-10 right-10 opacity-90" color="#C6D947" />

      <div className="relative w-full max-w-md">
        <Link to="/admin/login" className="inline-flex items-center gap-2 text-sm text-[#0F1B47] mb-6 font-semibold" data-testid="back-to-login">
          <ArrowLeft size={14} /> Back to sign in
        </Link>

        <div className="bg-[#FDFDF9] rounded-[32px] p-8 md:p-10 shadow-[0_30px_80px_-30px_rgba(15,27,71,0.25)] border border-[#0F1B47]/8">
          <div className="flex items-center gap-3 mb-6">
            <NaaviLogo height={36} />
          </div>
          <div className="inline-flex items-center gap-2 bg-[#F5F5F0] rounded-full px-3 py-1.5 mb-5">
            <ShieldCheck size={13} className="text-[#3AB0D6]" />
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#0F1B47]">Admin console</span>
          </div>

          {!sent ? (
            <>
              <h1 className="font-display text-3xl md:text-4xl font-medium text-[#0F1B47] leading-tight mb-2">Forgot your password?</h1>
              <p className="text-[#4A5568] text-sm font-light mb-8">
                Enter your staff email and we'll send a one-time reset link. The link is valid for 30 minutes.
              </p>
              <form onSubmit={submit} className="space-y-4" data-testid="admin-forgot-form">
                <Field label="Email">
                  <div className="relative">
                    <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#0F1B47]/50" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@naaviearlyyears.edu.in"
                      required
                      className={`${inputCls} pl-11`}
                      data-testid="admin-forgot-email"
                    />
                  </div>
                </Field>
                {error && (
                  <div className="text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-2xl px-4 py-3" data-testid="admin-forgot-error">{error}</div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#0F1B47] text-[#FDFDF9] rounded-full px-8 py-4 font-semibold text-[15px] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#1a2b6b] disabled:opacity-60"
                  data-testid="admin-forgot-submit"
                >
                  {loading ? "Sending…" : "Send reset link"}
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-2xl bg-[#5BC4B8]/15 text-[#5BC4B8] flex items-center justify-center mb-4">
                <CheckCircle2 size={22} />
              </div>
              <h1 className="font-display text-2xl md:text-3xl font-medium text-[#0F1B47] leading-tight mb-2">Check your inbox.</h1>
              <p className="text-[#4A5568] text-sm font-light mb-6">
                If an account with <b className="text-[#0F1B47]">{email}</b> exists, we just sent a reset link. It's valid for {ttl} minutes and can be used only once.
              </p>
              <p className="text-xs text-[#718096] font-light mb-6">
                Didn't get the email? Check your spam folder, or try again.
              </p>
              <button
                onClick={() => { setSent(false); setEmail(""); }}
                className="w-full bg-[#0F1B47]/5 text-[#0F1B47] rounded-full px-8 py-3.5 font-semibold text-sm hover:bg-[#0F1B47]/10"
                data-testid="admin-forgot-resend"
              >
                Try a different email
              </button>
            </>
          )}

          <div className="mt-6 pt-6 border-t border-[#0F1B47]/8">
            <p className="text-xs text-[#718096] font-light">
              Remembered it?{" "}
              <Link to="/admin/login" className="font-semibold text-[#0F1B47] underline underline-offset-2">Back to sign in →</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-[#F5F5F0] border border-[#0F1B47]/12 rounded-2xl px-5 py-3.5 text-[15px] text-[#0F1B47] placeholder:text-[#718096] font-medium focus:outline-none focus:border-[#648DE5] focus:ring-4 focus:ring-[#648DE5]/10 transition-all";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold tracking-[0.14em] uppercase text-[#0F1B47] mb-2 block">{label}</span>
      {children}
    </label>
  );
}
